import {FC} from 'react'
import {Trans, useTranslation} from '@/translations/i18n.tsx'
import {Flag01Icon} from '@components/ui/icon.tsx'
import {CategoryCard} from '@features/settings/components/category-card'
import {useFloorPlanOptin} from '@features/floor-plan/queries/useFloorPlanOptin.ts'
import {FloorPlanOptInButton} from '@features/floor-plan/components/floor-plan-optin-button'
import {
    FLOOR_PLAN_APP_TESTFLIGHT_URL,
    FLOOR_PLAN_LINK_CLASSNAME,
    FLOOR_PLAN_SCANNING_GUIDELINES_URL
} from '@features/floor-plan/constants.ts'
import {SETTINGS_SECTION_WRAPPER_CLASSNAME} from '@features/settings/constants.ts'
import {cn} from '@utilities/helpers'

const PROGRAM_HEADER_CLASSNAME = 'flex items-center gap-4 [&_h3]:text-md [&_h3]:font-semibold [&_h3]:text-neutral-900'
const PROGRAM_TEXT_CLASSNAME = 'text-sm text-neutral-700'

const OptedInContent: FC = () => {
    const {t} = useTranslation()
    return (
        <>
            <div className={PROGRAM_HEADER_CLASSNAME}>
                <h3>{t('floor_plan:title')}</h3>
                <span className="inline-flex items-center gap-1 text-sm font-bold text-neutral-900 before:h-1.5 before:w-1.5 before:rounded-full before:bg-success-600 before:content-['']">
                    {t('floor_plan:settings:opted_in')}
                </span>
            </div>
            <p className={PROGRAM_TEXT_CLASSNAME}>{t('floor_plan:settings:opted_in_description')}</p>
            <div className="flex flex-wrap gap-6">
                <a
                    className={cn(FLOOR_PLAN_LINK_CLASSNAME, 'text-md')}
                    href={FLOOR_PLAN_SCANNING_GUIDELINES_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {t('floor_plan:settings:scanning_guidelines')}
                </a>
                <a
                    className={cn(FLOOR_PLAN_LINK_CLASSNAME, 'text-md')}
                    href={FLOOR_PLAN_APP_TESTFLIGHT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {t('floor_plan:settings:get_app')}
                </a>
            </div>
        </>
    )
}

const NotOptedInContent: FC = () => {
    const {t} = useTranslation()
    return (
        <>
            <div className={PROGRAM_HEADER_CLASSNAME}>
                <h3>{t('floor_plan:title')}</h3>
            </div>
            <p className={PROGRAM_TEXT_CLASSNAME}>{t('floor_plan:banner:description')}</p>
            <p className={PROGRAM_TEXT_CLASSNAME}>
                <Trans
                    i18nKey="floor_plan:guidelines"
                    components={{
                        guidelines: (
                            <a
                                className={cn(FLOOR_PLAN_LINK_CLASSNAME, 'text-sm')}
                                href={FLOOR_PLAN_SCANNING_GUIDELINES_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                            />
                        )
                    }}
                />
            </p>
            <FloorPlanOptInButton from="settings" />
        </>
    )
}

/** "Programs & testings" settings section for the 3D floor plan early-access program. */
export const ProgramsSettings = () => {
    const {t} = useTranslation()
    const {isOptedIn} = useFloorPlanOptin()

    return (
        <div className={SETTINGS_SECTION_WRAPPER_CLASSNAME}>
            <CategoryCard
                icon={Flag01Icon}
                title={t('floor_plan:settings:category_title')}
                description={t('floor_plan:settings:category_description')}
            />
            <div className="flex flex-col items-start gap-3">
                {isOptedIn ? <OptedInContent /> : <NotOptedInContent />}
            </div>
        </div>
    )
}
